// OP-3605 — Regras puras da tela do pedido, espelho de `domain/solicitacao.ts`.
// São gates VISUAIS: a decisão é do servidor (podeTransicionar, podeExpedir,
// podeSepararMais). O que está aqui evita oferecer um botão que o servidor
// recusaria de qualquer jeito.
import { DestinoSaldo, EstoqueLocal, MetodoEnvio, SolicitacaoDetalhe, StatusSolicitacao } from '../../../../types/estoque'
import { faltaMotivo, textoRegime } from '../formato'
import { impedimentoDocumento } from '../documento'

/**
 * Espelho de `TRANSICOES` do domínio, entrada por entrada — inclusive as que
 * a tela nunca oferece (`transicoesManuais` faz o corte). Copiar só "o que
 * interessa" foi o defeito: `ATENDIDA_PARCIAL` ganhou CANCELADA, que o
 * domínio removeu de propósito (já houve entrega, e cancelar apagaria o
 * pedido que explica por que o equipamento saiu), e perdeu EM_ESPERA, que é
 * como o saldo espera uma transferência. O botão Cancelar falhava sempre e a
 * espera não podia ser pedida pela tela.
 */
export const TRANSICOES: Record<StatusSolicitacao, StatusSolicitacao[]> = {
  EM_ANALISE: ['AGUARDANDO_INFO', 'APROVADA', 'REJEITADA', 'CANCELADA'],
  AGUARDANDO_INFO: ['EM_ANALISE', 'CANCELADA'],
  APROVADA: ['EM_SEPARACAO', 'CANCELADA'],
  EM_SEPARACAO: ['PRONTA', 'EM_ESPERA', 'CONCLUIDA', 'CANCELADA'],
  EM_ESPERA: ['EM_SEPARACAO', 'PRONTA', 'CONCLUIDA', 'CANCELADA'],
  PRONTA: ['EXPEDIDA', 'EM_SEPARACAO', 'EM_ESPERA', 'CANCELADA'],
  EXPEDIDA: ['CONCLUIDA', 'ATENDIDA_PARCIAL', 'PRONTA'],
  ATENDIDA_PARCIAL: ['EM_SEPARACAO', 'EM_ESPERA', 'CONCLUIDA'],
  CONCLUIDA: [],
  REJEITADA: [],
  CANCELADA: [],
}
/** Só por efeito (expedir/entregar), nunca pela rota de status. */
export const STATUS_SO_POR_EFEITO: StatusSolicitacao[] = ['EXPEDIDA', 'ATENDIDA_PARCIAL']

/**
 * O que a logística pode pedir pela rota de status a partir de `de` — espelho
 * de `podeTransicionarManualmente`: EXPEDIDA e ATENDIDA_PARCIAL só acontecem
 * por efeito, CONCLUIDA a partir de EXPEDIDA é da entrega, e AGUARDANDO_INFO
 * tem rota própria (`pedirInformacao`).
 */
export function transicoesManuais(de: StatusSolicitacao): StatusSolicitacao[] {
  return (TRANSICOES[de] || []).filter((p) => STATUS_SO_POR_EFEITO.indexOf(p) < 0 && p !== 'AGUARDANDO_INFO')
    .filter((p) => !((p === 'CONCLUIDA' || p === 'PRONTA') && de === 'EXPEDIDA'))
}

export function avisoEquipamentoEmCampo(status: StatusSolicitacao, remessasAbertas?: Array<{ codigo: string }>): string | null {
  if (status !== 'CONCLUIDA' || !remessasAbertas || !remessasAbertas.length) return null
  return `Concluída na entrega · equipamento ainda em campo (${remessasAbertas.map((r) => r.codigo).join(', ')})`
}

/** Rejeitar, cancelar, aguardar transferência e concluir ou encerrar com saldo assinam uma decisão: exigem motivo. */
export function exigeMotivo(de: StatusSolicitacao, para: StatusSolicitacao, temSaldo: boolean): boolean {
  if (para === 'REJEITADA' || para === 'CANCELADA' || para === 'EM_ESPERA') return true
  if ((para === 'CONCLUIDA' || para === 'PRONTA') && temSaldo) return true
  return false
}

export function rotuloDoMotivo(de: StatusSolicitacao, temSaldo: boolean, oferecidas: StatusSolicitacao[]): string {
  const exigem = oferecidas.filter((p) => exigeMotivo(de, p, temSaldo)).map((p) => ROTULO_TRANSICAO[p].toLowerCase())
  if (!exigem.length) return '(opcional)'
  const lista = exigem.length === 1 ? exigem[0] : `${exigem.slice(0, -1).join(', ')} ou ${exigem[exigem.length - 1]}`
  return `(obrigatório para ${lista})`
}

/**
 * OP-3619 — a transição que faz o pedido andar, do estado em que ele está.
 *
 * É ela que ocupa a barra fixa do celular; as outras (recusar, cancelar,
 * aguardar transferência) ficam no menu. PRONTA não tem avanço por status:
 * expedir é formulário, não botão.
 */
const AVANCO: Partial<Record<StatusSolicitacao, StatusSolicitacao>> = {
  EM_ANALISE: 'APROVADA',
  AGUARDANDO_INFO: 'EM_ANALISE',
  APROVADA: 'EM_SEPARACAO',
  EM_SEPARACAO: 'PRONTA',
  EM_ESPERA: 'EM_SEPARACAO',
  ATENDIDA_PARCIAL: 'EM_SEPARACAO',
}

export function transicaoPrincipal(de: StatusSolicitacao): StatusSolicitacao | null {
  const para = AVANCO[de]
  if (!para) return null
  return transicoesManuais(de).indexOf(para) >= 0 ? para : null
}

export interface AcaoDoPedido {
  para: StatusSolicitacao
  rotulo: string
  impedimento: string | null
}

export const podeCancelarPedido = (c: { cancelamento?: { pode: boolean } | null }): boolean => c.cancelamento?.pode !== false

export function transicoesOferecidas(de: StatusSolicitacao, podeCancelar: boolean): StatusSolicitacao[] {
  return transicoesManuais(de).filter((p) => p !== 'CANCELADA' || podeCancelar)
}

export const impedimentoConfirmarCancelamentoPedido = (motivo: string): string | null =>
  faltaMotivo(motivo, 'Escreva o motivo acima: ele vai para o histórico e para quem pediu.')

export const cancelamentoBloqueadoPorEntrega = (c: { cancelamento?: { pode: boolean; impedimento: string | null } | null }): boolean =>
  c.cancelamento?.pode === false && c.cancelamento.impedimento === 'CANCELAMENTO_COM_ENTREGA_FEITA'

export const CONCLUEM_SEPARACAO: StatusSolicitacao[] = ['EM_SEPARACAO', 'EM_ESPERA']

export const ACEITAM_TRANSFERENCIA: StatusSolicitacao[] = ['APROVADA', 'EM_SEPARACAO', 'EM_ESPERA', 'PRONTA', 'ATENDIDA_PARCIAL']

export function rotaPedirTransferencia(
  s: Pick<SolicitacaoDetalhe, 'id' | 'status'>, l: { itemId: number; falta: number },
): string | null {
  if (ACEITAM_TRANSFERENCIA.indexOf(s.status) < 0 || l.falta <= 0) return null
  const q = new URLSearchParams({ requisicaoId: String(s.id), itemId: String(l.itemId), quantidade: String(l.falta) })
  return `/interno/estoque/transferencias/nova?${q.toString()}`
}

export const IMPEDIMENTO_FOTO_SEPARACAO = 'Envie a foto dos equipamentos separados.'

export function fotosPorRodada<F extends { enviadoEm: string }>(fotos: F[], inicioDaRodada: string | null | undefined): { daRodada: F[]; anteriores: F[] } {
  if (!inicioDaRodada) return { daRodada: fotos, anteriores: [] }
  return {
    daRodada: fotos.filter((f) => String(f.enviadoEm) >= inicioDaRodada),
    anteriores: fotos.filter((f) => String(f.enviadoEm) < inicioDaRodada),
  }
}

export function faltaFotoDaSeparacao(s: Pick<SolicitacaoDetalhe, 'status' | 'separacao'>): boolean {
  return !!s.separacao?.fotoObrigatoria && CONCLUEM_SEPARACAO.indexOf(s.status) >= 0 && Number(s.separacao.fotosDaRodada) <= 0
}

export function acoesDoPedido(de: StatusSolicitacao, temSaldo: boolean, motivo: string, podeCancelar = true, faltaFoto = false): { principal: AcaoDoPedido | null; outras: AcaoDoPedido[] } {
  const principal = transicaoPrincipal(de)
  const acao = (para: StatusSolicitacao): AcaoDoPedido => ({
    para,
    rotulo: ROTULO_TRANSICAO[para],
    impedimento: para === 'PRONTA' && faltaFoto ? IMPEDIMENTO_FOTO_SEPARACAO
      : exigeMotivo(de, para, temSaldo) && !motivo.trim() ? 'Escreva o motivo acima.' : null,
  })
  return {
    principal: principal ? acao(principal) : null,
    outras: transicoesOferecidas(de, podeCancelar).filter((p) => p !== principal).map(acao),
  }
}

export const ROTULO_TRANSICAO: Record<StatusSolicitacao, string> = {
  EM_ANALISE: 'Voltar para análise', AGUARDANDO_INFO: 'Pedir informação', APROVADA: 'Aprovar',
  EM_SEPARACAO: 'Iniciar separação', EM_ESPERA: 'Aguardar transferência', PRONTA: 'Concluir separação',
  EXPEDIDA: 'Expedir', ATENDIDA_PARCIAL: 'Atendida em parte', CONCLUIDA: 'Encerrar o pedido',
  REJEITADA: 'Recusar', CANCELADA: 'Cancelar pedido',
}

/**
 * OP-3619 — o teto do campo de separação de consumível.
 *
 * `separarQuantidade` SUBSTITUI a reserva da linha (`UPDATE ... SET
 * quantidade_separada = ?`), e o domínio valida com `separada: 0`: o valor
 * enviado é o total que vai ficar separado, não o que se acrescenta. Por isso
 * o teto é `pedida − atendida`, e não `falta` — que já desconta a reserva e
 * faria o campo parar antes do que o servidor aceita.
 */
export function limiteSeparacao(l: { pedida: number; atendida: number }): number {
  return Math.max(0, (Number(l.pedida) || 0) - (Number(l.atendida) || 0))
}

export interface LinhaCobertura {
  itemId: number
  nome: string
  controle: 'SERIAL' | 'QUANTIDADE'
  pedida: number
  separada: number
  atendida: number
  /** Ainda por separar nesta rodada. */
  falta: number
  /** Separado + atendido já cobre o pedido da linha. */
  coberta: boolean
}

/** Por item: pedido − atendido − separado; nunca negativo na tela. */
export function cobertura(s: Pick<SolicitacaoDetalhe, 'itens'>): LinhaCobertura[] {
  return s.itens.map((i) => {
    const pedida = Number(i.quantidade) || 0
    const separada = Number(i.quantidadeSeparada) || 0
    const atendida = Number(i.quantidadeAtendida) || 0
    const falta = Math.max(0, pedida - atendida - separada)
    return { itemId: i.itemId, nome: i.nome, controle: i.controle, pedida, separada, atendida, falta, coberta: falta === 0 }
  })
}

export interface TotalRegime { pedido: number; separada: number; atendida: number }

/** Peça e consumível somados cada um no seu regime — nunca num número só. */
export interface Totais {
  pecas: TotalRegime
  consumo: TotalRegime
  linhas: number
  linhasCobertas: number
}

export const TOTAIS_VAZIO: Totais = {
  pecas: { pedido: 0, separada: 0, atendida: 0 }, consumo: { pedido: 0, separada: 0, atendida: 0 }, linhas: 0, linhasCobertas: 0,
}

export function totais(s: Pick<SolicitacaoDetalhe, 'itens'>): Totais {
  const linhas = cobertura(s)
  const soma = (controle: LinhaCobertura['controle']): TotalRegime => linhas
    .filter((l) => l.controle === controle)
    .reduce((a, l) => ({ pedido: a.pedido + l.pedida, separada: a.separada + l.separada, atendida: a.atendida + l.atendida }), { pedido: 0, separada: 0, atendida: 0 })
  return { pecas: soma('SERIAL'), consumo: soma('QUANTIDADE'), linhas: linhas.length, linhasCobertas: linhas.filter((l) => l.coberta).length }
}

export interface Restante { pecas: number; consumo: number; total: number }

/** O que falta separar por regime; negativo é "separado além do pedido". */
export function restante(t: Totais): Restante {
  const pecas = t.pecas.pedido - t.pecas.atendida - t.pecas.separada
  const consumo = t.consumo.pedido - t.consumo.atendida - t.consumo.separada
  return { pecas, consumo, total: pecas + consumo }
}

export const textoRestante = (r: Restante): string => textoRegime({ pecas: Math.max(0, r.pecas), consumo: Math.max(0, r.consumo) })

export interface FormExpedicao {
  vinculoId: number | null
  metodoEnvio: MetodoEnvio | ''
  comprovanteKey: string | null
  transportadora: string
  codigoRastreio: string
  recebidoPor: string
  recebidoDocumento: string
  eventos: number[]
  destinoSaldo: DestinoSaldo | ''
  motivoSaldo: string
}

export const FORM_EXPEDICAO_VAZIO: FormExpedicao = {
  vinculoId: null, metodoEnvio: '', comprovanteKey: null, transportadora: '', codigoRastreio: '',
  recebidoPor: '', recebidoDocumento: '', eventos: [], destinoSaldo: '', motivoSaldo: '',
}

/**
 * Gate visual de `podeExpedir`: devolve o primeiro impedimento, ou null.
 *
 * `podeAnexar` entra aqui porque a expedição EXIGE comprovante e o comprovante
 * sobe por duas rotas de ESL18: quem não as tem não conclui a expedição pela
 * tela, e dizer isso antes é melhor do que deixar o upload falhar com
 * NOT_PERMITED depois de todo o formulário preenchido.
 */
export function impedimentoExpedicao(s: Pick<SolicitacaoDetalhe, 'status' | 'itens'>, f: FormExpedicao,
                                     capacidades?: { podeAnexar?: boolean }): string | null {
  if (s.status !== 'PRONTA') return 'O pedido precisa estar PRONTO para expedir.'
  const t = totais(s)
  if (t.pecas.separada + t.consumo.separada <= 0) return 'Nada separado para expedir.'
  if (capacidades && capacidades.podeAnexar === false && !f.comprovanteKey) {
    return 'A expedição exige o comprovante de despacho, e anexar depende da permissão "Anexos". Peça a quem tem a permissão para enviar o comprovante.'
  }
  if (!f.vinculoId) return 'Garanta o contrato do detentor antes de expedir.'
  if (!f.metodoEnvio) return 'Escolha o método de envio.'
  if (!f.comprovanteKey) return 'Envie o comprovante de despacho.'
  if (f.metodoEnvio === 'TRANSPORTADORA' && !f.transportadora.trim()) return 'Informe a transportadora.'
  if (f.metodoEnvio === 'TRANSPORTADORA' && f.codigoRastreio.trim().length > 80) return 'O código de rastreio tem no máximo 80 caracteres.'
  if (f.metodoEnvio === 'EQUIPE_PROPRIA') {
    const doc = impedimentoDocumento(f.recebidoDocumento, false)
    if (doc) return `Quem recebeu: ${doc}`
  }
  if (!f.eventos.length) return 'A remessa precisa atender pelo menos um evento.'
  const r = restante(t)
  if (r.total < 0) return 'Separado além do pedido.'
  if (r.total > 0) {
    if (!f.destinoSaldo) return `Faltam ${textoRestante(r)} para completar o pedido: decida o destino do saldo.`
    if (f.destinoSaldo === 'CANCELAR_SALDO' && !f.motivoSaldo.trim()) return 'Cancelar o saldo exige motivo.'
  }
  return null
}

/** `campos` pode chegar como objeto (driver JSON) ou string. */
export function lerCampos(v: Record<string, any> | string | null | undefined): Record<string, any> {
  if (!v) return {}
  if (typeof v === 'string') { try { return JSON.parse(v) || {} } catch { return {} } }
  return v
}


/**
 * O bloco de remessas do pedido continua na tela?
 *
 * Não pode depender de haver linhas NESTA página: quando a segunda página
 * falha, esconder o bloco leva junto os controles de voltar e o pedido passa
 * a parecer sem remessa nenhuma. Ele fica sempre que houver o que mostrar ou
 * o que consertar.
 */
export function mostrarBlocoRemessas(e: {
  linhas: number
  page: number
  total: number
  isLoading?: boolean
  isError?: unknown
}): boolean {
  return e.linhas > 0 || e.page > 1 || !!e.isError || !!e.isLoading || e.total > 0
}

export function destinosDoRedirecionamento(locais: EstoqueLocal[], pracaAtualId: number | null | undefined): EstoqueLocal[] {
  return locais.filter((l) => l.tipo === 'PRACA' && l.pracaId != null && l.pracaId !== pracaAtualId)
}

export function impedimentoRedirecionar(destinoId: number | null, motivo: string): string | null {
  if (!destinoId) return 'Escolha a praça que vai atender.'
  if (!motivo.trim()) return 'Escreva o motivo do redirecionamento.'
  return null
}
